import axios from "axios";
import { getHeadersConfig } from "../utils/config";
import { joinURLs } from "../utils/urlUtil";
import {
    AuthUser,
    AuthUserListResponse,
    Role,
    Permission,
    SecurityContext,
    ContextResource,
    TimezoneResponse
} from "../utils/types";

let abortController = new AbortController();

export const AuthUserService = {
    // User Management
    getUsers: async (): Promise<AuthUserListResponse> => {
        if (abortController) abortController.abort();
        abortController = new AbortController();

        const url = joinURLs('2/auth-user/users', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, {
            ...getHeadersConfig(),
            signal: abortController.signal
        });
        return response.data;
    },

    getUser: async (id: number): Promise<AuthUser> => {
        const url = joinURLs(`2/auth-user/users/${id}`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, getHeadersConfig());
        return response.data;
    },

    createUser: async (userData: {
        name: string;
        email: string;
        auth0: string;
        emailVerified: boolean;
        active?: number;
        timezoneId: number;
        roles?: { roleId: number; contextId?: number | null }[];
        permissions?: { permissionId: number; contextId?: number | null }[];
    }): Promise<AuthUser> => {
        const url = joinURLs('2/auth-user/users/create', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.post(url, userData, getHeadersConfig());
        return response.data;
    },

    updateUser: async (id: number, userData: {
        name: string;
        email: string;
        auth0: string;
        emailVerified: boolean;
        active?: number;
        timezoneId: number;
        roles?: { roleId: number; contextId?: number | null }[];
        permissions?: { permissionId: number; contextId?: number | null }[];
    }): Promise<AuthUser> => {
        const url = joinURLs(`2/auth-user/users/${id}/update`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.post(url, userData, getHeadersConfig());
        return response.data;
    },

    deleteUser: async (id: number): Promise<void> => {
        const url = joinURLs(`2/auth-user/users/${id}`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        await axios.delete(url, getHeadersConfig());
    },

    // Role Management
    getRoles: async (): Promise<Role[]> => {
        const url = joinURLs('2/auth-user/roles', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, getHeadersConfig());
        return response.data;
    },

    createRole: async (roleData: {
        name: string;
        slug: string;
        contextTypeId?: number | null;
        permissions?: { id: number }[];
    }): Promise<Role> => {
        const url = joinURLs('2/auth-user/roles/create', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.post(url, roleData, getHeadersConfig());
        return response.data;
    },

    updateRole: async (id: number, roleData: {
        name: string;
        slug: string;
        contextTypeId?: number | null;
        permissions?: { id: number }[];
    }): Promise<Role> => {
        const url = joinURLs(`2/auth-user/roles/${id}/update`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.post(url, roleData, getHeadersConfig());
        return response.data;
    },

    deleteRole: async (id: number): Promise<void> => {
        const url = joinURLs(`2/auth-user/roles/${id}`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        await axios.delete(url, getHeadersConfig());
    },

    // Permission Management
    getPermissions: async (): Promise<Permission[]> => {
        const url = joinURLs('2/auth-user/permissions', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, getHeadersConfig());
        return response.data;
    },
    
    // Security Context Management
    getSecurityContexts: async (): Promise<SecurityContext[]> => {
        const url = joinURLs('2/auth-user/context-types', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, getHeadersConfig());
        return response.data;
    },

    getContextResources: async (contextType: string): Promise<ContextResource[]> => {
        const url = joinURLs('2/auth-user/context-resources', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, {
            ...getHeadersConfig(),
            params: { contextType }
        });
        return response.data;
    },

    // User-Role Assignment
    assignRoleToUser: async (userId: number, roleId: number, contextIds?: number[] | null): Promise<void> => {
        const url = joinURLs(`2/auth-user/users/${userId}/roles/assign`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const params: any = { roleId };
        if (contextIds && contextIds.length > 0) {
            params.contextIds = contextIds.join(',');
        }
        await axios.get(url, {
            ...getHeadersConfig(),
            params
        });
    },

    removeRoleFromUser: async (userId: number, roleId: number, contextId?: number | null): Promise<void> => {
        const url = joinURLs(`2/auth-user/users/${userId}/roles/remove`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const params: any = { roleId };
        if (contextId !== null && contextId !== undefined) {
            params.contextId = contextId;
        }
        await axios.get(url, {
            ...getHeadersConfig(),
            params
        });
    },

    // User-Permission Assignment
    assignPermissionToUser: async (userId: number, permissionId: number, contextIds?: number[] | null): Promise<void> => {
        const url = joinURLs(`2/auth-user/users/${userId}/permissions/assign`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const params: any = { permissionId };
        if (contextIds && contextIds.length > 0) {
            params.contextIds = contextIds.join(',');
        }
        await axios.get(url, {
            ...getHeadersConfig(),
            params
        });
    },

    removePermissionFromUser: async (userId: number, permissionId: number, contextId?: number | null): Promise<void> => {
        const url = joinURLs(`2/auth-user/users/${userId}/permissions/remove`, process.env.REACT_APP_REPORTING_API_BASE_URL);
        const params: any = { permissionId };
        if (contextId !== null && contextId !== undefined) {
            params.contextId = contextId;
        }
        await axios.get(url, {
            ...getHeadersConfig(),
            params
        });
    },

    getTimezones: async (): Promise<TimezoneResponse[]> => {
        const url = joinURLs('2/auth-user/timezones', process.env.REACT_APP_REPORTING_API_BASE_URL);
        const response = await axios.get(url, getHeadersConfig());
        return response.data;
    }

};